import type { BusinessType, TradeTemplate } from '@buildbee/contracts';
import type { PipelineTemplate, ProjectTemplateSeed, StageTemplate } from './templates';

/**
 * Trade templates, ported from NorthOps' vertical packs ("hvac" and "home-remodeling-roofing" in
 * NorthOpsAI packages/db/src/seed.ts) and adapted to BuildBee: the receptionist's appointment types
 * become pipeline stages and project templates. The follow-up playbooks (win-back cohorts, message
 * sequences, job status texts) are carried over as data with `enabled: false`; nothing sends them
 * until a campaign engine reads them.
 */

export type FollowUpCohort = {
  key: string;
  label: string;
  /** What the cohort is built from: imported records (estimates, deferred repairs) or past jobs. */
  source: 'records' | 'last_appointment';
  recordType?: string;
  filters: { field: string; op: 'eq' | 'gte' | 'between'; value: unknown }[];
  sequenceKey: string;
  /** Average dollar value of a recovered item, for "money left on the table" reporting. */
  avgValue?: number;
};

export type FollowUpSequence = {
  key: string;
  steps: { channel: 'sms'; delayHours: number; template: string }[];
  maxTouches: number;
  throttlePerDay?: number;
};

export type StatusStage = { key: string; label: string; template: string };

export type FollowUpPlaybook = {
  enabled: false;
  throttlePerDay: number;
  cohorts: FollowUpCohort[];
  sequences: FollowUpSequence[];
  statusStages: StatusStage[];
};

export type TradeTemplateSeed = {
  id: TradeTemplate;
  name: string;
  blurb: string;
  /** The business type whose blueprint this trade builds on. */
  businessType: BusinessType;
  /** Pipelines added ahead of the business type's own; the first default one wins. */
  pipelines: PipelineTemplate[];
  /** Project templates added ahead of the business type's own (same name: the trade's wins). */
  projectTemplates: ProjectTemplateSeed[];
  /** Bookable visit types and FAQ from the NorthOps pack, for intake and the AI assistant. */
  intake: {
    services: { id: string; name: string; durationMinutes: number }[];
    faq: { question: string; answer: string }[];
  };
  followUps: FollowUpPlaybook;
  /** Where the content came from, so the NorthOps pack can be diffed later. */
  source: { system: 'northops'; vertical: string };
};

const won = (name = 'Won'): StageTemplate => ({
  name,
  kind: 'won',
  probabilityBps: 10_000,
  color: '#16a34a',
  rottingDays: null,
});
const lost = (name = 'Lost'): StageTemplate => ({
  name,
  kind: 'lost',
  probabilityBps: 0,
  color: '#dc2626',
  rottingDays: null,
});
const open = (
  name: string,
  probabilityBps: number,
  color: string,
  rottingDays: number | null = 14,
): StageTemplate => ({
  name,
  kind: 'open',
  probabilityBps,
  color,
  rottingDays,
});

const UNSOLD_ESTIMATE_STEPS = (project: string): FollowUpSequence['steps'] => [
  {
    channel: 'sms',
    delayHours: 0,
    template: `Hi {{contact_name}}, this is {{business_name}} following up on the estimate we gave you${project}. Still want to get that scheduled?`,
  },
  {
    channel: 'sms',
    delayHours: 72,
    template: `Following up on your open estimate. Is now a good time to get ${project ? 'your project' : 'it'} booked?`,
  },
  {
    channel: 'sms',
    delayHours: 168,
    template:
      'Just checking in one more time from {{business_name}}. Happy to answer any questions about your estimate.',
  },
];

const HVAC_PLUMBING: TradeTemplateSeed = {
  id: 'hvac_plumbing',
  name: 'HVAC / Plumbing',
  blurb: 'Service calls, tune-ups, installs and maintenance plans',
  businessType: 'specialty_sub',
  pipelines: [
    {
      name: 'Service & install leads',
      kind: 'homeowner_leads',
      isDefault: true,
      stages: [
        open('New call', 1000, '#64748b', 1),
        open('Diagnostic booked', 3000, '#0ea5e9', 3),
        open('Quote sent', 5500, '#f59e0b', 7),
        open('Follow-up', 7000, '#a855f7', 14),
        won('Booked'),
        lost('Lost'),
      ],
    },
    {
      name: 'Maintenance plans',
      kind: 'general',
      isDefault: false,
      stages: [
        open('Renewal due', 3000, '#0ea5e9', 30),
        open('Offer sent', 6000, '#f59e0b', 14),
        won('Renewed'),
        lost('Lapsed'),
      ],
    },
  ],
  projectTemplates: [
    {
      name: 'Service call / diagnostic',
      description: 'Diagnose and fix on the first visit where possible.',
      projectType: 'service',
      phases: [
        {
          name: 'Diagnostic visit',
          durationDays: 1,
          offsetDays: 0,
          tasks: [{ name: 'Diagnose and quote', durationDays: 1 }],
        },
      ],
      costCodes: [
        { code: '100', name: 'Labor' },
        { code: '200', name: 'Material' },
        { code: '450', name: 'Trip / diagnostic fee' },
      ],
      documentFolders: ['photos', 'general'],
      punchChecklist: ['System running to spec', 'Work area cleaned', 'Customer walked through the fix'],
    },
    {
      name: 'Maintenance tune-up',
      description: 'Seasonal or annual maintenance visit.',
      projectType: 'service',
      phases: [{ name: 'Tune-up visit', durationDays: 1, offsetDays: 0, tasks: [] }],
      costCodes: [
        { code: '100', name: 'Labor' },
        { code: '200', name: 'Material' },
      ],
      documentFolders: ['photos', 'general'],
      punchChecklist: ['Filters replaced', 'Safety checks logged', 'Deferred repairs noted for follow-up'],
    },
    {
      name: 'Emergency repair',
      description: 'No heat, no cooling or an active leak: same-day response.',
      projectType: 'service',
      phases: [{ name: 'Emergency visit', durationDays: 1, offsetDays: 0, tasks: [] }],
      costCodes: [
        { code: '100', name: 'Labor' },
        { code: '200', name: 'Material' },
        { code: '460', name: 'After-hours premium' },
      ],
      documentFolders: ['photos', 'general'],
      punchChecklist: ['Hazard made safe', 'Permanent repair quoted if needed'],
    },
    {
      name: 'Equipment installation',
      description: 'Furnace, AC, heat pump or water heater replacement.',
      projectType: 'trade_package',
      phases: [
        {
          name: 'Quote & equipment order',
          durationDays: 7,
          offsetDays: 0,
          tasks: [{ name: 'Permit application', durationDays: 3 }],
        },
        { name: 'Removal & install', durationDays: 2, offsetDays: 7, tasks: [] },
        {
          name: 'Start-up & inspection',
          durationDays: 2,
          offsetDays: 9,
          tasks: [{ name: 'Permit inspection', durationDays: 1 }],
        },
      ],
      costCodes: [
        { code: '22-000', name: 'Plumbing' },
        { code: '23-000', name: 'HVAC' },
        { code: '100', name: 'Labor' },
        { code: '200', name: 'Equipment & material' },
        { code: '400', name: 'Permits & fees' },
      ],
      documentFolders: ['contracts', 'permits', 'photos', 'warranties', 'general'],
      punchChecklist: [
        'Equipment registered for warranty',
        'Old equipment hauled away',
        'Thermostat programmed',
        'Customer walkthrough',
      ],
    },
  ],
  intake: {
    services: [
      { id: 'service-call', name: 'Service Call / Diagnostic', durationMinutes: 60 },
      { id: 'maintenance-tuneup', name: 'Maintenance Tune-Up', durationMinutes: 45 },
      { id: 'emergency-repair', name: 'Emergency Repair', durationMinutes: 90 },
      { id: 'installation-quote', name: 'Installation Quote', durationMinutes: 30 },
    ],
    faq: [
      {
        question: 'Do you offer financing?',
        answer: 'Yes, we offer financing plans for larger installations. Ask your technician for details.',
      },
      {
        question: "What's your service area?",
        answer: 'We service the whole metro area and surrounding suburbs.',
      },
      {
        question: 'Do you offer a maintenance plan?',
        answer: 'Yes, our annual membership plan includes two tune-ups a year and priority scheduling.',
      },
      {
        question: 'Are you licensed and insured?',
        answer: "Yes, we're fully licensed, bonded, and insured.",
      },
    ],
  },
  followUps: {
    enabled: false,
    throttlePerDay: 50,
    cohorts: [
      {
        key: 'unsold_estimates',
        label: 'Unsold estimates',
        source: 'records',
        recordType: 'estimate',
        filters: [
          { field: 'value', op: 'gte', value: 500 },
          { field: 'age_days', op: 'between', value: [7, 90] },
          { field: 'status', op: 'eq', value: 'open' },
        ],
        sequenceKey: 'unsold_estimates_seq',
        avgValue: 800,
      },
      {
        key: 'estimate_expiring',
        label: 'Estimates expiring soon',
        source: 'records',
        recordType: 'estimate',
        filters: [
          { field: 'age_days', op: 'between', value: [-30, 0] },
          { field: 'status', op: 'eq', value: 'open' },
        ],
        sequenceKey: 'estimate_expiring_seq',
        avgValue: 800,
      },
      {
        key: 'deferred_repairs',
        label: 'Deferred repairs',
        source: 'records',
        recordType: 'deferred_repair',
        filters: [
          { field: 'age_days', op: 'between', value: [14, 120] },
          { field: 'status', op: 'eq', value: 'open' },
        ],
        sequenceKey: 'deferred_repairs_seq',
        avgValue: 650,
      },
      {
        key: 'maintenance_due',
        label: 'Maintenance due',
        source: 'last_appointment',
        filters: [{ field: 'days_since_last_appointment', op: 'between', value: [335, 395] }],
        sequenceKey: 'maintenance_due_seq',
      },
    ],
    sequences: [
      { key: 'unsold_estimates_seq', steps: UNSOLD_ESTIMATE_STEPS(''), maxTouches: 3, throttlePerDay: 25 },
      {
        key: 'deferred_repairs_seq',
        steps: [
          {
            channel: 'sms',
            delayHours: 0,
            template:
              '{{business_name}} here. Before it gets busier this season, want us to take care of that repair we flagged, {{contact_name}}?',
          },
          {
            channel: 'sms',
            delayHours: 120,
            template:
              'Following up on the repair our technician noted last visit. Want to get that scheduled?',
          },
        ],
        maxTouches: 2,
        throttlePerDay: 25,
      },
      {
        key: 'maintenance_due_seq',
        steps: [
          {
            channel: 'sms',
            delayHours: 0,
            template:
              "Hi {{contact_name}}, it's about time for your annual tune-up with {{business_name}}. Want us to get you on the schedule?",
          },
        ],
        maxTouches: 1,
      },
      {
        key: 'estimate_expiring_seq',
        steps: [
          {
            channel: 'sms',
            delayHours: 0,
            template:
              'Hi {{contact_name}}, your estimate from {{business_name}} expires soon. Call us to lock in your price.',
          },
          {
            channel: 'sms',
            delayHours: 72,
            template:
              'Reminder from {{business_name}}: your estimate expires shortly. Want us to get it scheduled?',
          },
        ],
        maxTouches: 2,
        throttlePerDay: 25,
      },
    ],
    statusStages: [
      {
        key: 'scheduled',
        label: 'Scheduled',
        template: 'Your appointment with {{business_name}} is confirmed.',
      },
      {
        key: 'technician_en_route',
        label: 'Technician en route',
        template: 'Your technician from {{business_name}} is on the way!',
      },
      { key: 'job_started', label: 'Job started', template: 'Your technician has started the job.' },
      {
        key: 'job_completed',
        label: 'Job completed',
        template: 'Your job is complete! Thanks for choosing {{business_name}}.',
      },
      {
        key: 'invoice_sent',
        label: 'Invoice sent',
        template: 'Your invoice from {{business_name}} has been sent. Thanks again for your business.',
      },
    ],
  },
  source: { system: 'northops', vertical: 'hvac' },
};

const REMODELING_ROOFING: TradeTemplateSeed = {
  id: 'remodeling_roofing',
  name: 'Home remodeling / Roofing',
  blurb: 'In-home estimates, roof inspections and storm work',
  businessType: 'remodeler',
  pipelines: [
    {
      name: 'Remodel & roofing leads',
      kind: 'homeowner_leads',
      isDefault: true,
      stages: [
        open('New', 1000, '#64748b', 2),
        open('Estimate booked', 3000, '#0ea5e9', 5),
        open('Inspection / measure', 4500, '#6366f1', 7),
        open('Proposal sent', 6500, '#f59e0b', 14),
        open('Financing / insurance', 8000, '#a855f7', 21),
        won(),
        lost(),
      ],
    },
  ],
  projectTemplates: [
    {
      name: 'Roof replacement',
      description: 'Tear-off and re-roof, from permit to final inspection.',
      projectType: 'remodel',
      phases: [
        {
          name: 'Inspection & measure',
          durationDays: 3,
          offsetDays: 0,
          tasks: [{ name: 'Roof inspection', durationDays: 1 }],
        },
        {
          name: 'Permit & material order',
          durationDays: 10,
          offsetDays: 3,
          tasks: [
            { name: 'Permit application', durationDays: 5 },
            { name: 'Material delivery', durationDays: 1 },
          ],
        },
        {
          name: 'Tear-off & install',
          durationDays: 4,
          offsetDays: 13,
          tasks: [{ name: 'Deck inspection', durationDays: 1 }],
        },
        {
          name: 'Cleanup & final inspection',
          durationDays: 2,
          offsetDays: 17,
          tasks: [
            { name: 'Magnet sweep for nails', durationDays: 1 },
            { name: 'Final inspection', durationDays: 1 },
          ],
        },
      ],
      costCodes: [
        { code: '01-000', name: 'General conditions' },
        { code: '02-100', name: 'Tear-off & disposal' },
        { code: '07-300', name: 'Shingles & roofing' },
        { code: '07-600', name: 'Flashing & sheet metal' },
        { code: '07-700', name: 'Gutters & downspouts' },
        { code: '400', name: 'Permits & fees' },
      ],
      documentFolders: ['contracts', 'permits', 'photos', 'warranties', 'insurance', 'closeout', 'general'],
      punchChecklist: [
        'Nails swept from yard and driveway',
        'Flashing sealed',
        'Gutters clear',
        'Before/after photos taken',
        'Warranty registered',
      ],
    },
    {
      name: 'Storm damage repair',
      description: 'Insurance-backed repair: document damage, then fix.',
      projectType: 'remodel',
      phases: [
        { name: 'Emergency tarp & damage photos', durationDays: 1, offsetDays: 0, tasks: [] },
        {
          name: 'Insurance documentation',
          durationDays: 14,
          offsetDays: 1,
          tasks: [{ name: 'Adjuster meeting', durationDays: 1 }],
        },
        { name: 'Repair', durationDays: 5, offsetDays: 15, tasks: [] },
        { name: 'Final inspection & invoice', durationDays: 2, offsetDays: 20, tasks: [] },
      ],
      costCodes: [
        { code: '01-000', name: 'General conditions' },
        { code: '01-500', name: 'Temporary protection' },
        { code: '07-300', name: 'Roofing' },
        { code: '09-000', name: 'Interior finishes' },
      ],
      documentFolders: ['contracts', 'photos', 'insurance', 'closeout', 'general'],
      punchChecklist: [
        'Damage photos filed with claim',
        'Interior water damage checked',
        'Final photos taken',
      ],
    },
  ],
  intake: {
    services: [
      { id: 'in-home-estimate', name: 'In-Home Estimate', durationMinutes: 60 },
      { id: 'roof-inspection', name: 'Roof Inspection', durationMinutes: 45 },
      { id: 'project-consult', name: 'Project Consultation', durationMinutes: 45 },
    ],
    faq: [
      {
        question: 'Do you offer financing?',
        answer: 'Yes, we offer financing options for larger projects. Ask your estimator for details.',
      },
      {
        question: 'Are you licensed and insured?',
        answer: "Yes, we're fully licensed, bonded, and insured.",
      },
      {
        question: 'How long does a typical project take?',
        answer:
          'It varies by project scope. Your estimator will give you a realistic timeline during your estimate visit.',
      },
      {
        question: 'Do you help with insurance claims for storm damage?',
        answer:
          'We can document damage for your insurance claim, but the claim itself is between you and your insurer. Ask your estimator for details.',
      },
    ],
  },
  followUps: {
    enabled: false,
    throttlePerDay: 30,
    cohorts: [
      {
        key: 'unsold_estimates',
        label: 'Unsold estimates',
        source: 'records',
        recordType: 'estimate',
        filters: [
          { field: 'value', op: 'gte', value: 1000 },
          { field: 'age_days', op: 'between', value: [7, 90] },
          { field: 'status', op: 'eq', value: 'open' },
        ],
        sequenceKey: 'unsold_estimates_seq',
        avgValue: 3200,
      },
    ],
    sequences: [
      {
        key: 'unsold_estimates_seq',
        steps: UNSOLD_ESTIMATE_STEPS(' for your project'),
        maxTouches: 3,
        throttlePerDay: 20,
      },
    ],
    statusStages: [
      {
        key: 'estimate_scheduled',
        label: 'Estimate scheduled',
        template: 'Your estimate visit with {{business_name}} is confirmed.',
      },
      {
        key: 'project_started',
        label: 'Project started',
        template: 'Your project with {{business_name}} has started.',
      },
      {
        key: 'inspection_passed',
        label: 'Inspection passed',
        template: 'Great news: your project passed inspection!',
      },
      {
        key: 'project_complete',
        label: 'Project complete',
        template: 'Your project is complete! Thanks for choosing {{business_name}}.',
      },
      {
        key: 'final_invoice_sent',
        label: 'Final invoice sent',
        template: 'Your final invoice from {{business_name}} has been sent. Thanks again for your business.',
      },
    ],
  },
  source: { system: 'northops', vertical: 'home-remodeling-roofing' },
};

const TRADES: Record<TradeTemplate, TradeTemplateSeed> = {
  hvac_plumbing: HVAC_PLUMBING,
  remodeling_roofing: REMODELING_ROOFING,
};

export function tradeTemplateSeed(trade: TradeTemplate): TradeTemplateSeed {
  return TRADES[trade];
}

export const ALL_TRADE_TEMPLATES: TradeTemplateSeed[] = Object.values(TRADES);
